/**
 * The careers page (owner, 2026-10-19): the openings published in the Admin (Configurações › Vagas do site) are read at
 * runtime from the public route, so opening or closing a position needs no site deploy. While none is open — or the read
 * fails — the page shows the market's plain text ("no openings yet"). Each opening: team, title, type and city (or the
 * whole country), summary, the full description on demand, and one apply action — the opening's own address, or the
 * Central chat. Strings are the market's injected copy; colours are tokens.
 */
import { type ReactElement, useEffect, useState } from 'react';
import type { SiteCareersPage } from '@vello/web-entry';
import { careerOpeningsOf, careerOpeningsUrl, type CareerOpeningView } from '../src/careers.js';
import { SiteLinkTo, useSiteNav } from './SiteChrome';
import { Callout } from './SitePages';
import { Icon } from './icons';

/** Read the open positions of the visitor's market; [] until (and unless) the platform answers with some. */
function useOpenings(apiBaseUrl: string, country: string): readonly CareerOpeningView[] | undefined {
  const [openings, setOpenings] = useState<readonly CareerOpeningView[] | undefined>(undefined);
  useEffect(() => {
    const url = careerOpeningsUrl(apiBaseUrl, country);
    if (url === undefined || typeof fetch === 'undefined') {
      setOpenings([]);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      let status = 0;
      let body: unknown;
      try {
        const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
        status = res.status;
        body = await res.json();
      } catch {
        /* offline / blocked / not JSON — the page keeps its plain text */
      }
      if (!cancelled) setOpenings(careerOpeningsOf(status, body));
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, country]);
  return openings;
}

export function CareersPage({ page: p }: { page: SiteCareersPage }): ReactElement {
  const { apiBaseUrl, country } = useSiteNav();
  const openings = useOpenings(apiBaseUrl, country);
  const live = p.openings !== undefined && openings !== undefined && openings.length > 0 ? openings : undefined;
  return (
    <div className="band-inner text-page">
      <div className="text-head">
        <span className="eyebrow">{p.eyebrow}</span>
        <h1 className="page-title page-title--md">{p.title}</h1>
      </div>
      {live !== undefined && p.openings !== undefined ? (
        <>
          <div className="prose">
            <p>{p.openings.intro}</p>
          </div>
          <section className="careers" aria-labelledby="careers-list-title">
            <h2 id="careers-list-title" className="section-title section-title--sm">{p.openings.listTitle}</h2>
            <ul className="careers-list">
              {live.map((o) => (
                <li key={o.openingId} className="career-card">
                  <span className="eyebrow">{o.team}</span>
                  <h3 className="career-title">{o.title}</h3>
                  <span className="career-meta">{o.employmentType} · {o.marketName ?? p.openings?.countryWide}</span>
                  <p className="career-summary">{o.summary}</p>
                  <details className="career-details">
                    <summary className="career-more">
                      <span>{p.openings?.readMore}</span>
                      <span className="faq-chevron"><Icon name="chevron-down" size={18} /></span>
                    </summary>
                    <p className="career-description">{o.description}</p>
                  </details>
                  <SiteLinkTo target={o.applyUrl ?? 'contact'} className="btn btn--primary career-apply">{p.openings?.applyLabel}</SiteLinkTo>
                </li>
              ))}
            </ul>
          </section>
        </>
      ) : (
        <div className="prose">
          {p.paragraphs.map((para) => <p key={para}>{para}</p>)}
        </div>
      )}
      {p.cta !== undefined && <Callout callout={p.cta} tone="mist" />}
    </div>
  );
}
